import React, { useContext, useState } from 'react'
import { Formik } from 'formik'
import { Button, Form, NotificationInline, NotificationManagerContext,
   Spacer, Text, View } from 'oio-react'
import PropTypes from 'prop-types'
import { useParams } from 'react-router-dom'
import { CloseIcon } from 'assets/icons'
import { Input, Textarea } from 'src/components/ConnectedForm'
import { useOrganization, useUpdateInitiative } from 'src/graphql/hooks'
import { InitiativeHierarchyContext } from 'src/libraries/contexts/InitiativeHierarchy'
import ThemeContext from 'src/libraries/contexts/Theme'

const CustomMetadataFieldUpsert = ({ onCloseButtonClick, onSave }) => {
   const { metadataKey, operationName } = useParams()
   const [error, setError] = useState(null)
   const { showNotification } = useContext(NotificationManagerContext)
   const theme = useContext(ThemeContext)

   const { organization } = useOrganization()
   const { initiative } = useContext(InitiativeHierarchyContext)
   const { updateInitiative } = useUpdateInitiative()
   const currentField = organization.metadata.find(field => field.key === metadataKey) || {}

   const initialFormValues = {
      key: '', // Key cannot be changed once set. Field only applies on create
      value: currentField.value || '',
      description: currentField.description || ''
   }

   const handleUpdateInitiative = async (values, actions) => {
      const formData = values

      if (operationName === 'update') {
         formData.key = metadataKey
      }

      try {
         await updateInitiative({ id: initiative.id }, {
            metadata: [{
               operation: operationName,
               [operationName]: {
                  ...formData
               }
            }]
         })

         showNotification({
            message: 'Metadata saved successfully',
            title: 'Success!',
            type: 'success'
         })

         onSave()
      } catch (err) {
         setError(err?.graphQLErrors?.map(e => e.message).join(', ') || err.message)
      } finally {
         actions.setSubmitting(false)
      }
   }

   return (
      <>
         <View
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            height="50px"
            padding="0px 15px"
            borderBottom="1px solid #eee">
            <Text size="2.5" weight="medium">
               Edit Metadata Field
            </Text>
            <View onClick={onCloseButtonClick}>
               <CloseIcon
                  width="20px"
                  height="20px"
                  strokeWidth="2px"
                  color="#aaa"
               />
            </View>
         </View>
         {error && (
            <NotificationInline
               textSize="1.5"
               type="error"
               title="An error occured"
               message={error}
            />
         )}
         <View padding="15px">
            <Formik
               initialValues={initialFormValues}
               onSubmit={handleUpdateInitiative}
               render={({ handleSubmit, isSubmitting }) => (
                  <Form
                     elementAppearance="plain"
                     elementBackgroundColor="#eee"
                     elementFocusBackgroundColor="#f3f3f3"
                     onSubmit={handleSubmit}>
                     <View>
                        <Text size="1.5" color="#444" weight="medium">
                           Key
                        </Text>
                        {operationName === 'create' && (
                           <>
                              <Text size="1.5" color="#888">
                                 (cannot be changed once set)
                              </Text>
                              <Spacer size="2" />
                              <Input name="key" size="md" maxLength="30" />
                           </>
                        )}
                        {operationName === 'update' && (
                           <>
                              <Spacer size="1" />
                              <View
                                 display="inline-block"
                                 padding="2px 3px"
                                 backgroundColor="rgba(99, 255, 241, 0.2)">
                                 <Text
                                    fontFamily="courier"
                                    size="1.5"
                                    weight="bold"
                                    color="#333">
                                    {metadataKey}
                                 </Text>
                              </View>
                           </>
                        )}
                        <Spacer size="2" />
                     </View>
                     <View>
                        <Text size="1.5" color="#444" weight="medium">
                           Value
                        </Text>
                        <Spacer size="2" />
                        <Input name="value" size="md" maxLength="1024" />
                        <Spacer size="2" />
                     </View>
                     <View>
                        <Text size="1.5" color="#444" weight="medium">
                           Description
                        </Text>
                        <Text size="1.5" color="#888">
                           (short description explaining the purpose of this metadata field)
                        </Text>
                        <Spacer size="2" />
                        <Textarea name="description" size="sm" rows="3" />
                        <Spacer size="2" />
                     </View>
                     <View display="flex" justifyContent="flex-end">
                        <Button
                           width="50%"
                           type="submit"
                           name="Save"
                           mode={isSubmitting ? 'loading' : 'normal'}
                           color={theme.protoSettingsHighlightBackgroundColor}
                           textColor={theme.protoSettingsHighlightForegroundColor}
                           textWeight="medium"
                           size="md"
                           textSize="0.9"
                           padding="25px"
                        />
                     </View>
                  </Form>
               )}
            />
         </View>
      </>
   )
}

CustomMetadataFieldUpsert.propTypes = {
   onCloseButtonClick: PropTypes.func.isRequired,
   onSave: PropTypes.func.isRequired
}

export default CustomMetadataFieldUpsert
